import axios from 'axios';
import {
  getToken,
  setToken,
  getTokenMeta as readTokenMeta,
  setTokenMeta,
  clearToken,
} from './tokenStore';
import { getApiBaseUrl } from './runtimeConfig';

export interface TokenMeta {
  userId: number;
  email: string;
  displayName: string;
  roles: string[];
  issuedAtUtc: string;
  expiresAtUtc: string | null;
}

export interface DevLoginRequest {
  userId?: number;
  email?: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  roles?: string[];
  scopes?: string[];
  expirationMinutes?: number;
}

interface DevTokenResponse {
  token: string;
  payload: string;
  signature: string;
  issuedAtUtc: string;
  expiresAtUtc: string | null;
  userId: number;
  email: string;
  displayName: string;
  roles: string[];
}

/** Store a token and its metadata from a dev token response. */
function storeToken(response: DevTokenResponse): void {
  // Strip "Bearer " prefix if present — the API returns the full scheme+token
  // but the apiClient interceptor adds "Bearer " itself.
  const token = response.token.startsWith('Bearer ')
    ? response.token.slice(7)
    : response.token;
  setToken(token);
  const meta: TokenMeta = {
    userId: response.userId,
    email: response.email,
    displayName: response.displayName,
    roles: response.roles,
    issuedAtUtc: response.issuedAtUtc,
    expiresAtUtc: response.expiresAtUtc,
  };
  setTokenMeta(meta);
}

/** Store a raw token string along with manually provided metadata. */
export function storeRawToken(token: string, meta: TokenMeta): void {
  const cleaned = token.startsWith('Bearer ') ? token.slice(7) : token;
  setToken(cleaned);
  setTokenMeta(meta);
}

/** Get the current access token from in-memory storage (null if not present). */
export function getAccessToken(): string | null {
  return getToken();
}

/** Get the stored token metadata (null if not present). */
export function getTokenMeta(): TokenMeta | null {
  return readTokenMeta();
}

/** Check if the stored token is expired. Returns true if expired or no token. */
export function isTokenExpired(): boolean {
  const meta = getTokenMeta();
  if (!meta) return true;
  if (!meta.expiresAtUtc) return false; // no expiry = never expires
  return new Date(meta.expiresAtUtc) <= new Date();
}

/** Check if the user is authenticated (token present and not expired). */
export function isAuthenticated(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  return !isTokenExpired();
}

/**
 * Login using the dev token endpoint (development only).
 * Generates a token via POST /api/dev/token and stores it.
 */
export async function devLogin(request?: DevLoginRequest): Promise<TokenMeta> {
  const response = await axios.post<DevTokenResponse>(
    `${getApiBaseUrl()}/dev/token`,
    request ?? {},
  );
  const data = response.data;
  storeToken(data);
  return {
    userId: data.userId,
    email: data.email,
    displayName: data.displayName,
    roles: data.roles,
    issuedAtUtc: data.issuedAtUtc,
    expiresAtUtc: data.expiresAtUtc,
  };
}

/** Clear all stored auth state. */
export function logout(): void {
  clearToken();
}
